// Workspace-side client hooks (share, feature/task actions, PRD versions, workspace rename).
// Endpoint contracts follow spec/04; shapes of the newer task endpoints follow the agreed body contracts.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, qk, type FeatureDto, type PlanListItem, type TaskDto } from './api'
import type { TaskLayer, TaskPriority, TaskStatus } from './types'

/** Same cache entry as `usePlans`, but gated — for surfaces that may render for anonymous visitors. */
export const usePlansWhen = (enabled: boolean) =>
  useQuery({ queryKey: qk.plans, queryFn: () => api<{ plans: PlanListItem[] }>('/api/plan/list'), enabled })

export interface ShareDto {
  slug: string
  url: string
  isActive: boolean
  viewCount: number
}

export const useCreateShare = (planId: string) =>
  useMutation({ mutationFn: () => api<{ share: ShareDto }>(`/api/plan/share/${planId}`, { method: 'POST' }) })

export const useFeedbackEligibility = (context: string, enabled = true) =>
  useQuery({
    queryKey: ['feedback', 'eligibility', context] as const,
    queryFn: () => api<{ eligible: boolean }>(`/api/feedback/eligibility?context=${encodeURIComponent(context)}`),
    enabled,
    staleTime: 60_000,
  })

export function useUpdateFeature(planId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string; name?: string; summary?: string; phase?: number; icon?: string; spec?: string }) =>
      api<{ feature: FeatureDto }>(`/api/plan/features/${id}`, { method: 'PATCH', json: patch }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.features(planId) })
      qc.invalidateQueries({ queryKey: qk.subFeatures(planId) })
    },
  })
}

export function useDeleteFeature(planId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<{ ok: true }>(`/api/plan/features/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.features(planId) })
      qc.invalidateQueries({ queryKey: qk.subFeatures(planId) })
      qc.invalidateQueries({ queryKey: qk.tasks(planId) })
    },
  })
}

export function useCreateTask(planId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { featureId: string; title: string; layer: TaskLayer; priority: TaskPriority }) =>
      api<{ task: TaskDto }>('/api/plan/tasks/create', { method: 'POST', json: { planId, ...body } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.tasks(planId) }),
  })
}

export function useResetTasks(planId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api<{ updated?: number }>('/api/plan/tasks/reset', { method: 'POST', json: { planId } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.tasks(planId) }),
  })
}

export function useGenerateTasks(planId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (featureId: string) => api<{ tasks?: TaskDto[] }>('/api/plan/tasks/generate', { method: 'POST', json: { planId, featureId } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.tasks(planId) }),
  })
}

export function useUpdateTask(planId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string; status?: TaskStatus; title?: string; priority?: TaskPriority }) =>
      api<{ task: TaskDto }>(`/api/plan/tasks/${id}`, { method: 'PATCH', json: patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.tasks(planId) }),
  })
}

export function useSaveDocument(planId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (content: string) => api<{ document?: unknown }>('/api/plan/save-document', { method: 'POST', json: { planId, content } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.plan(planId) }),
  })
}

export function useRenameWorkspace(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (name: string) => api<{ workspace: { id: string; name: string } }>(`/api/workspaces/${id}`, { method: 'PATCH', json: { name } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.wsPlans(id) })
      qc.invalidateQueries({ queryKey: qk.plans })
    },
  })
}

export const useReversePlan = (id: string) =>
  useMutation({ mutationFn: () => api<{ planId: string }>(`/api/workspaces/${id}/reverse-plan`, { method: 'POST', json: {} }) })
